<script setup>
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import LabelTagIcon from 'dashboard/components-next/label/LabelTagIcon.vue';

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  variant: {
    type: String,
    default: 'default',
  },
  /**
   * Aceleres: mostra o caixote para APAGAR a etiqueta de vez.
   * Nao confundir com clicar na linha, que so tira desta conversa.
   */
  deletable: {
    type: Boolean,
    default: false,
  },
  deleteLabelText: {
    type: String,
    default: '',
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  priorityIcon: {
    type: String,
    default: '',
  },
});

defineEmits(['delete']);

const textHeartGlyph = '♥︎';

const isClassIcon = computed(() =>
  String(props.option?.icon || '').startsWith('i-')
);
</script>

<template>
  <component
    :is="mobile && !deletable ? 'button' : 'div'"
    class="menu min-h-9 min-w-0 max-w-full text-n-slate-12"
    :class="{ 'border-0 bg-transparent text-left': mobile && !deletable }"
    :type="mobile && !deletable ? 'button' : undefined"
    :role="mobile && !deletable ? undefined : 'button'"
  >
    <!-- Aceleres 2026-08-17: o conjunto fluent do painel tem 204 icones e nao
         traz encaminhar, alfinete nem faisca. Quando o icone vem como CLASSE
         (`i-lucide-*`, `i-fluent-*`) quem o desenha e o <Icon>; os nomes
         antigos continuam a passar pelo <fluent-icon>.

         As duas trilhas usam a MESMA medida, 16px. Aqui o fluent ficou um
         tempo em 14, para compensar que ele e preenchido e o lucide e a traco
         — a ideia era igualar o peso visto, nao a caixa. Na tela o que se
         notou foi a diferenca de tamanho, entao a caixa passou a ser igual
         para todos (pedido 2026-08-17). Mexer num dos dois numeros sem o outro
         traz o desencontro de volta. -->
    <template
      v-if="
        variant === 'icon' && option.icon === 'i-tabler-corner-up-right-double'
      "
    >
      <span
        class="i-lucide-forward size-[18px] flex-shrink-0 md:hidden"
        aria-hidden="true"
      />
      <Icon
        :icon="option.icon"
        class="hidden size-[22px] flex-shrink-0 text-black md:inline-flex"
      />
    </template>
    <Icon
      v-else-if="variant === 'icon' && isClassIcon"
      :icon="option.icon"
      class="size-[18px] flex-shrink-0"
    />
    <span
      v-else-if="variant === 'icon' && option.icon === 'acl-text-heart'"
      aria-hidden="true"
      class="inline-flex size-[18px] flex-shrink-0 items-center justify-center text-lg leading-none"
    >
      {{ textHeartGlyph }}
    </span>
    <LabelTagIcon
      v-else-if="variant === 'icon' && option.icon === 'tag'"
      :size="16"
    />
    <fluent-icon
      v-else-if="variant === 'icon' && option.icon"
      :icon="option.icon"
      size="16"
      class="flex-shrink-0"
    />
    <LabelTagIcon
      v-if="
        (variant === 'label' || variant === 'label-assigned') && option.color
      "
      :color="option.color"
      :size="16"
    />
    <Avatar
      v-if="variant === 'agent'"
      :name="option.label"
      :src="option.thumbnail"
      :status="option.status === 'online' ? option.status : null"
      :size="20"
      class="flex-shrink-0"
    />
    <CardPriorityIcon v-if="priorityIcon" :priority="priorityIcon" />
    <p class="menu-label truncate min-w-0 flex-1">
      {{ option.label }}
    </p>
    <Icon
      v-if="variant === 'label-assigned'"
      icon="i-lucide-check"
      class="flex-shrink-0 size-3.5 mr-1"
    />
    <!-- Aceleres: caixote e nao "x" — clicar na LINHA ja significa tirar a
         etiqueta desta conversa; o caixote diz que ela deixa de existir. -->
    <!-- `tabindex="-1"` e `@mousedown.prevent` nao sao zelo a mais: o menu de
         contexto fecha no @blur do proprio contentor (ContextMenu.vue), e um
         <button> ROUBA o foco ao ser clicado. O menu fechava no mousedown,
         este botao saia do DOM antes do mouseup, e o evento `click` nunca
         chegava a existir — o caixote parecia morto. Os itens vizinhos nao
         sofrem disso porque sao `div role="button"`, que nao recebem foco.
         O prevent no mousedown impede a mudanca de foco e o clique completa. -->
    <button
      v-if="deletable"
      type="button"
      tabindex="-1"
      class="acl-menu-delete"
      :aria-label="deleteLabelText"
      :title="deleteLabelText"
      @mousedown.prevent
      @click.stop="$emit('delete')"
    >
      <Icon icon="i-lucide-trash-2" class="size-3.5" />
    </button>
  </component>
</template>

<style scoped lang="scss">
/* Aceleres 2026-08-17: menu maior a pedido. Os três menus que usam este item
   crescem juntos — o da conversa, o da mensagem e o da caixa de entrada —
   porque é o mesmo componente e tê-los com tamanhos diferentes seria pior. */
.menu {
  width: 15rem;
  @apply flex items-center flex-nowrap py-1.5 px-2 rounded-md overflow-hidden cursor-pointer;

  .menu-label {
    @apply my-0 mx-2.5 text-sm;
  }

  &:hover {
    @apply bg-n-brand text-white;
  }
}

.agent-thumbnail {
  margin-top: 0 !important;
}

/* Aceleres: o caixote herda a cor do texto da linha — que fica branco quando o
   rato esta sobre ela — e so avermelha no seu proprio hover. */
.acl-menu-delete {
  @apply flex-shrink-0 inline-flex items-center justify-center p-1 mr-0.5 rounded border-0 bg-transparent cursor-pointer opacity-0;
  color: inherit;
  transition:
    opacity 0.12s ease,
    color 0.12s ease;
}

.menu:hover .acl-menu-delete {
  @apply opacity-100;
}

.acl-menu-delete:hover {
  color: #fecaca;
}

@media (hover: none) {
  .acl-menu-delete {
    @apply opacity-100;
  }
}
</style>
